import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import Header from '../Common/Header';
import { api } from '../../services/api';
import stampImage from '../../assets/stamps/b2world-stamp.png';
import './VoterCertificate.css';

// HELPER FUNCTION to convert an image URL to a Data URL (Base64)
const toDataURL = (url) =>
  fetch(url)
    .then((response) => response.blob())
    .then(
      (blob) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        })
    );

const VoterCertificate = () => {
  const { t } = useTranslation();
  const [showCertificate, setShowCertificate] = useState(false);
  const [formData, setFormData] = useState({
    voterId: '',
    fullName: '',
    phoneNumber: '',
    age: '',
    gender: '',
    district: '',
    constituency: '',
    selfieImage: null,
    selfiePreview: null,
  });

  const [certificate, setCertificate] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [constituencies, setConstituencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingConstituencies, setLoadingConstituencies] = useState(false);
  const [error, setError] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  
  const [stampDataUrl, setStampDataUrl] = useState(null);

  useEffect(() => {
    loadDistricts();
    toDataURL(stampImage)
      .then(dataUrl => setStampDataUrl(dataUrl))
      .catch(err => console.error("Failed to load stamp image:", err));
  }, []);

  const loadDistricts = async () => {
    try {
      setLoading(true);
      const response = await api.getDistricts();
      const data = Array.isArray(response) ? response : response?.data || [];
      setDistricts(data);
    } catch (error) {
      console.error('❌ Error loading districts:', error);
      setDistricts([]);
    } finally {
      setLoading(false);
    }
  };

  const loadConstituenciesByDistrict = async (district) => {
    try {
      setLoadingConstituencies(true);
      const response = await api.getConstituenciesByDistrict(district);
      const data = Array.isArray(response) ? response : response?.data || [];
      setConstituencies(data);
    } catch (error) {
      console.error('❌ Error loading constituencies:', error);
      setConstituencies([]);
    } finally {
      setLoadingConstituencies(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const handleDistrictChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value, constituency: '' }));
    if (value) {
      loadConstituenciesByDistrict(value);
    } else {
      setConstituencies([]);
    }
  };

  const handleSelfieUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { setError(t('voterCertificate.imageSizeError') || 'Image size must not exceed 5MB'); return; }
      if (!file.type.startsWith('image/')) { setError(t('voterCertificate.imageTypeError') || 'Only image files are allowed'); return; }
      const reader = new FileReader();
      reader.onloadend = () => { setFormData((prev) => ({ ...prev, selfieImage: file, selfiePreview: reader.result })); setError(''); };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.fullName || !formData.phoneNumber || !formData.age || !formData.gender || !formData.district || !formData.constituency || !formData.selfiePreview) {
      setError(t('voterCertificate.fillAllFields') || 'Please fill all required fields marked with *');
      window.scrollTo(0, 0);
      return;
    }
    setSubmitLoading(true);
    setError('');
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('voterId', formData.voterId || '');
      formDataToSend.append('fullName', formData.fullName.trim());
      formDataToSend.append('phoneNumber', formData.phoneNumber.trim());
      formDataToSend.append('age', String(formData.age));
      formDataToSend.append('gender', formData.gender.trim());
      formDataToSend.append('district', formData.district.trim());
      formDataToSend.append('constituency', formData.constituency.trim());
      formDataToSend.append('selfieImage', formData.selfieImage);
      const response = await api.uploadVoterCertificate(formDataToSend);
      if (response.success) {
        const certificateData = {
          voterId: response.data?.voterId || formData.voterId || 'GENERATED',
          fullName: response.data?.fullName || formData.fullName,
          phoneNumber: response.data?.phoneNumber || formData.phoneNumber,
          age: response.data?.age || formData.age,
          gender: response.data?.gender || formData.gender,
          district: response.data?.district || formData.district,
          constituency: response.data?.constituency || formData.constituency,
          certificateId: response.certificateId || response.data?.certificateId,
          date: new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' }),
          time: new Date().toLocaleTimeString('en-IN'),
          selfiePreview: formData.selfiePreview,
        };
        setCertificate(certificateData);
        setShowCertificate(true);
        window.scrollTo(0, 0);
      } else {
        setError(response.message || t('voterCertificate.generateError') || 'Failed to generate certificate');
        window.scrollTo(0, 0);
      }
    } catch (error) {
      console.error('❌ Error:', error);
      setError(error.message || t('common.connectionError') || 'Connection error. Please try again.');
      window.scrollTo(0, 0);
    } finally {
      setSubmitLoading(false);
    }
  };

  // Reusable function to generate the certificate canvas
  const generateCertificateCanvas = async () => {
    const originalElement = document.getElementById('certificate-image-container');
    if (!originalElement) throw new Error('Certificate element not found.');
    const clone = originalElement.cloneNode(true);
    clone.style.position = 'absolute';
    clone.style.top = '0';
    clone.style.left = '0';
    clone.style.width = '1000px'; 
    clone.style.height = '700px';
    clone.style.zIndex = '-9999';
    clone.style.margin = '0';
    clone.style.padding = '0';
    document.body.appendChild(clone);
    await new Promise(resolve => setTimeout(resolve, 100));
    const canvas = await html2canvas(clone, { scale: 2, backgroundColor: '#fffef8', logging: false, useCORS: true });
    document.body.removeChild(clone);
    return canvas;
  };

  // Download function uses the helper
  const downloadCertificate = async () => {
    setDownloadLoading(true);
    try {
      const canvas = await generateCertificateCanvas();
      const link = document.createElement('a');
      link.download = `Voter-Certificate-${certificate.certificateId || 'CERT'}.png`;
      link.href = canvas.toDataURL('image/png', 1.0);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('❌ Download error:', error);
      alert(t('voterCertificate.downloadError') || 'Failed to download certificate. Please try again.');
    } finally {
      setDownloadLoading(false);
    }
  };

  // New function to handle sharing using Web Share API
  const handleShare = async () => {
    setShareLoading(true);
    try {
      const canvas = await generateCertificateCanvas();
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      
      const file = new File([blob], `Voter-Certificate-${certificate.certificateId}.png`, { type: 'image/png' });
      const shareData = {
        files: [file],
        title: t('voterCertificate.shareTitle', 'My Voter Participation Certificate'),
        text: t('voterCertificate.shareText', 'I participated in the Bihar Election 2025 voter survey!'),
      };

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share(shareData);
      } else {
        alert(t('voterCertificate.shareFallback', "Your browser doesn't support image sharing. Please download the certificate to share it."));
        await downloadCertificate();
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('❌ Share error:', error);
        alert(t('voterCertificate.shareError', 'Could not share certificate. Please try downloading it instead.'));
      }
    } finally {
      setShareLoading(false);
    }
  };

  if (showCertificate && certificate) {
    if (!stampDataUrl) {
      return <div>Loading Certificate...</div>;
    }

    return (
      <>
        <Header />
        <div className="voter-certificate-page">
          <div className="cert-header">
             <h1><i className="fas fa-award" aria-hidden="true"></i> {t('voterCertificate.yourCertificate') || 'Your Certificate'}</h1>
          </div>

          <div className="cert-display-card">
            <p className="success-message">
              <i className="fas fa-check-circle" aria-hidden="true"></i>
              {t('voterCertificate.generatedSuccessfully') || 'Certificate generated successfully!'}
            </p>
            
            <div id="certificate-image-container" className="certificate-image-wrapper">
              <svg viewBox="0 0 1000 700" xmlns="http://www.w3.org/2000/svg" className="certificate-svg">
                <rect width="1000" height="700" fill="#fffef8" /><rect x="20" y="20" width="960" height="660" fill="none" stroke="#d4a574" strokeWidth="8"/><rect x="35" y="35" width="930" height="630" fill="none" stroke="#e8d4b8" strokeWidth="2"/><g stroke="#d4a574" strokeWidth="3"><line x1="50" y1="50" x2="110" y2="50"/><line x1="50" y1="50" x2="50" y2="110"/><line x1="950" y1="50" x2="890" y2="50"/><line x1="950" y1="50" x2="950" y2="110"/><line x1="50" y1="650" x2="110" y2="650"/><line x1="50" y1="650" x2="50" y2="590"/><line x1="950" y1="650" x2="890" y2="650"/><line x1="950" y1="650" x2="950" y2="590"/></g><text x="500" y="80" fontSize="42" fontWeight="bold" textAnchor="middle" fill="#2c3e50" fontFamily="serif">CERTIFICATE OF PARTICIPATION</text><text x="500" y="110" fontSize="15" textAnchor="middle" fill="#666" fontFamily="serif">Bihar Legislative Assembly Elections 2025</text><line x1="150" y1="125" x2="850" y2="125" stroke="#d4a574" strokeWidth="2"/><text x="500" y="155" fontSize="13" textAnchor="middle" fill="#555" fontFamily="sans-serif" fontStyle="italic">This is to certify that</text><text x="500" y="190" fontSize="32" fontWeight="bold" textAnchor="middle" fill="#2c3e50" fontFamily="serif">{certificate.fullName.toUpperCase()}</text><line x1="250" y1="200" x2="750" y2="200" stroke="#d4a574" strokeWidth="2"/><g><rect x="80" y="230" width="150" height="180" fill="#f9f9f9" rx="8"/><rect x="80" y="230" width="150" height="180" fill="none" stroke="#d4a574" strokeWidth="3" rx="8"/><defs><clipPath id="photoClip"><rect x="90" y="240" width="130" height="160" rx="6"/></clipPath></defs><image x="90" y="240" width="130" height="160" href={certificate.selfiePreview} preserveAspectRatio="xMidYMid slice" clipPath="url(#photoClip)"/><text x="155" y="425" fontSize="10" textAnchor="middle" fill="#666" fontFamily="sans-serif" fontWeight="bold">Photo ID</text></g><g><text x="280" y="260" fontSize="12" fill="#333" fontFamily="sans-serif" fontWeight="bold">Age: <tspan fill="#667eea">{certificate.age}</tspan><tspan> | Gender: </tspan><tspan fill="#667eea">{certificate.gender}</tspan></text><text x="280" y="290" fontSize="12" fill="#333" fontFamily="sans-serif" fontWeight="bold">Phone: <tspan fill="#667eea">{certificate.phoneNumber}</tspan></text><text x="280" y="320" fontSize="12" fill="#333" fontFamily="sans-serif" fontWeight="bold">District: <tspan fill="#667eea">{certificate.district}</tspan></text><text x="280" y="350" fontSize="12" fill="#333" fontFamily="sans-serif" fontWeight="bold">Constituency: <tspan fill="#667eea">{certificate.constituency}</tspan></text></g><text x="500" y="465" fontSize="12" textAnchor="middle" fill="#555" fontFamily="sans-serif" fontStyle="italic">has participated in the voter survey for Bihar Legislative Assembly Elections 2025</text><g><text x="500" y="500" fontSize="11" textAnchor="middle" fill="#666" fontFamily="sans-serif">Date: <tspan fontWeight="bold">{certificate.date}</tspan></text><text x="500" y="520" fontSize="11" textAnchor="middle" fill="#666" fontFamily="sans-serif">Time: <tspan fontWeight="bold">{certificate.time}</tspan></text><text x="500" y="540" fontSize="9" textAnchor="middle" fill="#999" fontFamily="monospace">Certificate ID: {certificate.certificateId}</text></g><g><image x="770" y="530" width="130" height="100" href={stampDataUrl} preserveAspectRatio="xMidYMid meet" /><text x="835" y="640" fontSize="9" textAnchor="middle" fill="#333" fontFamily="sans-serif" fontWeight="bold">Official Seal</text></g>
              </svg>
            </div>

            <div className="certificate-actions">
              <button className="cert-btn primary" onClick={downloadCertificate} disabled={downloadLoading} title={t('voterCertificate.downloadCertificate') || 'Download Certificate'}>
                <i className={`fas fa-${downloadLoading ? 'spinner fa-spin' : 'download'}`} aria-hidden="true"></i>
                {downloadLoading ? t('common.downloading') || 'Downloading...' : t('voterCertificate.downloadCertificate') || 'Download Certificate'}
              </button>
              
              {navigator.share && (
                <button className="cert-btn share" onClick={handleShare} disabled={shareLoading} title={t('common.share', 'Share')}>
                  <i className={`fas fa-${shareLoading ? 'spinner fa-spin' : 'share-alt'}`} aria-hidden="true"></i>
                  {shareLoading ? t('common.sharing', 'Sharing...') : t('common.share', 'Share')}
                </button>
              )}
              
              <button className="cert-btn secondary" onClick={() => window.location.reload()} title={t('voterCertificate.createAnother') || 'Create Another Certificate'}>
                <i className="fas fa-redo" aria-hidden="true"></i>
                {t('voterCertificate.createAnother') || 'Create Another'}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="voter-certificate-page">
        <div className="cert-header">
            <h1><i className="fas fa-edit" aria-hidden="true"></i> {t('voterCertificate.title') || 'Voter Participation Certificate'}</h1>
            <p>{t('voterCertificate.subtitle') || 'Generate your voter certificate with selfie'}</p>
        </div>
        <div className="cert-form-card">
          <form onSubmit={handleSubmit}>
            {error && ( <div className="error-message" role="alert"><i className="fas fa-exclamation-circle" aria-hidden="true"></i> {error}</div>)}
            <div className="form-group"><label>{t('voterCertificate.uploadSelfie') || 'Upload Selfie'} *</label><div className="selfie-upload-section">{formData.selfiePreview ? (<div className="selfie-preview"><img src={formData.selfiePreview} alt="Selfie" /><button type="button" className="change-photo-btn" onClick={() => document.getElementById('selfie-input').click()}><i className="fas fa-camera" aria-hidden="true"></i> {t('voterCertificate.changePhoto') || 'Change Photo'}</button></div>) : (<div className="selfie-upload-box" onClick={() => document.getElementById('selfie-input').click()} role="button" tabIndex={0}><i className="fas fa-cloud-upload-alt" aria-hidden="true"></i><p>{t('voterCertificate.clickToUpload') || 'Click to upload selfie'}</p><small>{t('voterCertificate.imageSpecs') || 'JPG or PNG, max 5MB'}</small></div>)}<input id="selfie-input" type="file" accept="image/*" capture="user" onChange={handleSelfieUpload} style={{ display: 'none' }} aria-label={t('voterCertificate.uploadSelfie') || 'Upload Selfie'}/></div></div>
            <div className="form-group"><label>{t('voterCertificate.fullName') || 'Full Name'} *</label><input type="text" name="fullName" value={formData.fullName} onChange={handleChange} required placeholder={t('voterCertificate.enterFullName') || 'Enter your full name'} aria-label={t('voterCertificate.fullName') || 'Full Name'}/></div>
            <div className="form-group"><label>{t('voterCertificate.phone') || 'Phone Number'} *</label><input type="tel" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} required placeholder={t('voterCertificate.tenDigitNumber') || '10 digit number'} pattern="[0-9]{10}" aria-label={t('voterCertificate.phone') || 'Phone Number'}/></div>
            <div className="form-group"><label>{t('voterCertificate.age') || 'Age'} *</label><input type="number" name="age" value={formData.age} onChange={handleChange} required min="18" max="120" placeholder={t('voterCertificate.ageRange') || '18-120'} aria-label={t('voterCertificate.age') || 'Age'}/></div>
            <div className="form-group"><label>{t('voterCertificate.gender') || 'Gender'} *</label><select name="gender" value={formData.gender} onChange={handleChange} required aria-label={t('voterCertificate.gender') || 'Gender'}><option value="">{t('voterCertificate.selectGender') || 'Select Gender'}</option><option value="Male">{t('voterCertificate.male') || 'Male'}</option><option value="Female">{t('voterCertificate.female') || 'Female'}</option><option value="Other">{t('voterCertificate.other') || 'Other'}</option></select></div>
            <div className="form-group"><label>{t('voterCertificate.district') || 'District'} * {loading && <span className="loading-small">{t('common.loading') || 'Loading...'}</span>}</label><select name="district" value={formData.district} onChange={handleDistrictChange} required disabled={loading || districts.length === 0} aria-label={t('voterCertificate.district') || 'District'}><option value="">{t('voterCertificate.selectDistrict') || 'Select District'}</option>{districts.map((d, i) => (<option key={i} value={d}>{d}</option>))}</select></div>
            <div className="form-group"><label>{t('voterCertificate.constituency') || 'Constituency'} * {loadingConstituencies && <span className="loading-small">{t('common.loading') || 'Loading...'}</span>}</label><select name="constituency" value={formData.constituency} onChange={handleChange} required disabled={loadingConstituencies || constituencies.length === 0 || !formData.district} aria-label={t('voterCertificate.constituency') || 'Constituency'}><option value="">{!formData.district ? t('voterCertificate.selectDistrictFirst') || 'Select District First' : t('voterCertificate.selectConstituency') || 'Select Constituency'}</option>{constituencies.map(c => (<option key={c.id} value={c.name}>{c.code} - {c.name}</option>))}</select></div>
            <div className="form-group"><label>{t('voterCertificate.voterId') || 'Voter ID'} ({t('common.optional') || 'Optional'})</label><input type="text" name="voterId" value={formData.voterId} onChange={handleChange} placeholder={t('voterCertificate.enterVoterId') || 'Enter voter ID or leave blank'} maxLength="10" aria-label={t('voterCertificate.voterId') || 'Voter ID'}/><small>{t('voterCertificate.leaveBlank') || 'Leave blank - Voter ID will be auto-generated'}</small></div>
            <button type="submit" className="cert-btn primary large" disabled={submitLoading} style={{ marginTop: '20px' }} title={t('voterCertificate.generateCertificate') || 'Generate Certificate'}>{submitLoading ? (<><i className="fas fa-spinner fa-spin" aria-hidden="true"></i> {t('voterCertificate.generatingCertificate') || 'Generating Certificate...'}</>) : (<><i className="fas fa-certificate" aria-hidden="true"></i> {t('voterCertificate.generateCertificate') || 'Generate Certificate'}</>)}</button>
          </form>
        </div>
      </div>
    </>
  );
};

export default VoterCertificate;